/**
 * Route guard for the consumer application.
 *
 * This is a user-experience convenience only — it stops a signed-out visitor
 * seeing an empty shell. Actual authorization happens on the API for every
 * request, so bypassing this in the browser gains nothing.
 */

import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../providers/AuthProvider.jsx';
import { PageLoader } from '../feedback/PageLoader.jsx';
import { AccountUnavailable } from '../feedback/AccountUnavailable.jsx';

const ONBOARDING_PATH = '/app/onboarding';

export function ProtectedRoute({ children }) {
  const { isAuthenticated, initialized, profile, profileMissing, syncError } = useAuth();
  const location = useLocation();

  // Wait for the first auth check before deciding, or a returning user would
  // be bounced to /login on every refresh.
  if (!initialized) return <PageLoader />;

  if (!isAuthenticated) {
    // Remember where they were headed so sign-in can return them there.
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  /*
   * Authenticated with Firebase but no server-side profile. Firebase succeeding
   * is not the same as being signed in to this application: without the profile
   * nothing account-scoped works, so the app must not render.
   *
   * Gated on `syncError` rather than `profileMissing` alone. The two states are
   * not the same — `completeSignIn` bails early when a sync is already in
   * flight (its `syncingRef` guard), so a second auth-state event can set
   * `initialized` while the profile is legitimately still on its way. Blocking
   * on the absence of a profile would flash this screen during that race;
   * blocking on a recorded failure only fires once sync has actually failed.
   */
  if (profileMissing) {
    return syncError ? <AccountUnavailable /> : <PageLoader />;
  }

  /*
   * First-run setup, gated on the profile rather than the Firebase user: if
   * `/auth/sync` has not landed yet there are no preferences to read, and
   * bouncing to onboarding would only show a form that cannot save.
   *
   * Skipping stamps the timestamp too, so declining is remembered.
   */
  const needsOnboarding = Boolean(profile) && !profile.preferences?.onboardingCompletedAt;

  if (needsOnboarding && location.pathname !== ONBOARDING_PATH) {
    return <Navigate to={ONBOARDING_PATH} replace />;
  }

  return children;
}
